// 09-10 (maqueta aprobada «‹ atrás»): en el móvil, deslizar el dedo desde el borde izquierdo vuelve a la lista,
// como en Telegram/WhatsApp en iPhone. La PWA instalada no tiene el gesto de «atrás» de Safari: este es el suyo.
// Con eventos táctiles (no de puntero): en iOS un `pointercancel` corta el gesto en cuanto la conversación
// empieza a desplazarse; los táctiles siguen llegando.
import { useEffect, useRef } from 'react';

/** El dedo tiene que empezar pegado al borde (no robar un deslizar normal dentro del chat)… */
export const BORDE_PX = 20;
/** …y recorrer esto en horizontal, más que en vertical (un desplazamiento hacia abajo no cuenta). */
export const RECORRIDO_PX = 80;

export interface Punto {
  x: number;
  y: number;
}

/** ¿Este gesto (de `inicio` a `ahora`) es «volver atrás»? Puro: lo usa el hook y lo prueban los tests. */
export function esGestoAtras(inicio: Punto, ahora: Punto): boolean {
  if (inicio.x > BORDE_PX) return false;
  const dx = ahora.x - inicio.x;
  const dy = Math.abs(ahora.y - inicio.y);
  return dx > RECORRIDO_PX && dx > dy;
}

/** ¿Hay una capa encima del chat (diálogo, visor, menú desplegado) o empieza el dedo dentro de una? */
export function hayAlgoEncima(objetivo: EventTarget | null): boolean {
  if (document.querySelector('[role="dialog"], [role="menu"]')) return true;
  return objetivo instanceof Element && !!objetivo.closest('[data-radix-popper-content-wrapper]');
}

export function useDeslizarAtras(activo: boolean, alAtras: () => void): void {
  // El último `alAtras` sin volver a registrar los oyentes en cada render.
  const atras = useRef(alAtras);
  useEffect(() => {
    atras.current = alAtras;
  });
  useEffect(() => {
    if (!activo) return;
    let inicio: Punto | null = null;
    const empieza = (e: TouchEvent) => {
      const dedo = e.touches[0];
      // Revisión 09-10: con algo encima (visor de imagen, diálogo, menú), el gesto es de eso, no del chat de debajo.
      if (hayAlgoEncima(e.target)) {
        inicio = null;
        return;
      }
      inicio = e.touches.length === 1 && dedo && dedo.clientX <= BORDE_PX ? { x: dedo.clientX, y: dedo.clientY } : null;
    };
    const mueve = (e: TouchEvent) => {
      const dedo = e.touches[0];
      if (!inicio || !dedo) return;
      if (esGestoAtras(inicio, { x: dedo.clientX, y: dedo.clientY })) {
        inicio = null;
        atras.current();
      }
    };
    const suelta = () => {
      inicio = null;
    };
    window.addEventListener('touchstart', empieza, { passive: true });
    window.addEventListener('touchmove', mueve, { passive: true });
    window.addEventListener('touchend', suelta);
    window.addEventListener('touchcancel', suelta);
    return () => {
      window.removeEventListener('touchstart', empieza);
      window.removeEventListener('touchmove', mueve);
      window.removeEventListener('touchend', suelta);
      window.removeEventListener('touchcancel', suelta);
    };
  }, [activo]);
}
